import * as React from "react";
import { $, merge } from "glamor";
import ErrorOutlineOutlinedIcon from "@material-ui/icons/ErrorOutlineOutlined";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import ErrorOutlineRoundedIcon from "@material-ui/icons/ErrorOutlineRounded";
import CheckCircleOutlinedIcon from "@material-ui/icons/CheckCircleOutlined";
import CloseOutlinedIcon from "@material-ui/icons/CloseOutlined";
import { IconButton } from "material-ui";
import classNames from "classnames";
import { themeOptions } from "styles/theme";
import { SeverityMessages } from "constants/product-collection";

interface IOwnProps {
    message: React.ReactNode;
    customIcon?: React.ReactNode;
    open: boolean;
    severity: SeverityMessages;
    noMargin?: boolean;
    onClose?: () => void;
    className?: string;
    customStyles?: object;
    dataTest?: string;
    resultMessage?: string;
    resultValue?: string;
}

export enum AlertCloseMode {
    None = "None",
    Manually = "Manually",
    Automaticlly = "Automatically",
}

const alertColorsBySeverityHash = {
    [SeverityMessages.Error]: {
        backgroundColor: themeOptions.palette.errorAlertBackground,
        borderColor: themeOptions.palette.errorAlertBorderColor,
        textColor: themeOptions.palette.errorAlertTextColor,
        iconColor: themeOptions.palette.errorAlertBorderColor,
        alertIcon: ErrorOutlineOutlinedIcon,
    },
    [SeverityMessages.Warning]: {
        backgroundColor: themeOptions.palette.warningAlertBackground,
        borderColor: themeOptions.palette.warningAlertBorderColor,
        textColor: themeOptions.palette.warningAlertTextColor,
        iconColor: themeOptions.palette.warningAlertTextColor,
        alertIcon: ReportProblemOutlinedIcon,
    },
    [SeverityMessages.Info]: {
        backgroundColor: themeOptions.palette.infoAlertBackground,
        borderColor: themeOptions.palette.infoAlertBorderColor,
        textColor: themeOptions.palette.infoAlertTextColor,
        iconColor: themeOptions.palette.infoAlertTextColor,
        alertIcon: ErrorOutlineRoundedIcon,
    },
    [SeverityMessages.Success]: {
        backgroundColor: themeOptions.palette.successAlertBackground,
        borderColor: themeOptions.palette.successAlertBorderColor,
        textColor: themeOptions.palette.successAlertTextColor,
        iconColor: themeOptions.palette.successAlertTextColor,
        alertIcon: CheckCircleOutlinedIcon,
    },
};

export const AlertNotification: React.FC<IOwnProps> = ({
    message,
    noMargin,
    open,
    onClose,
    className,
    customStyles,
    severity = SeverityMessages.Warning,
    dataTest,
    resultMessage,
    resultValue,
    customIcon,
}) => {
    const alertSettings = alertColorsBySeverityHash[severity];
    if (!alertSettings) {
        return null;
    }

    const [isOpen, setOpen] = React.useState<boolean>(open); // eslint-disable-line react-hooks/rules-of-hooks

    // eslint-disable-next-line react-hooks/rules-of-hooks
    React.useEffect(() => {
        setOpen(open);
    }, [open]);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const styles = React.useMemo(
        () =>
            merge(
                $(".container", {
                    backgroundColor: "transparent",
                    display: "flex",
                    alignItems: "flex-start",
                    padding: "8px",
                    width: `calc(100% - ${noMargin ? "0px" : "24px"})`,
                    margin: noMargin ? "0px" : "12px",
                    borderRadius: "4px",
                }),
                $(".severity", {
                    backgroundColor: alertSettings.backgroundColor,
                    border: `1px solid ${alertSettings.borderColor}`,
                    color: alertSettings.textColor,
                }),
                $(" .icon", {
                    marginRight: "12px",
                    padding: "3px 0",
                    display: "flex",
                    opacity: 0.9,
                }),
                $(" .message", {
                    fontSize: "14px",
                    flexGrow: 2,
                    padding: "6px 0",
                }),
                $(" .action_wrapper", {
                    display: "flex",
                    alignItems: "end",
                    flexDirection: "column",
                }),
                $(" .result_wrapper", {
                    display: "flex",
                    alignItems: "end",
                    flexDirection: "column",
                }),
                $(" .result_message", {
                    fontSize: "16px",
                }),
                $(" .result_value", {
                    fontWeight: "bold",
                    fontSize: "20px",
                }),
                $(" .action", {
                    display: "flex",
                    paddingLeft: "8px",
                }),
                customStyles,
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [noMargin, severity],
    );

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const handleCloseNotification = React.useCallback(() => {
        if (onClose) {
            onClose();
        }
    }, [onClose]);

    if (!isOpen) {
        return null;
    }

    return (
        <div {...styles} className={classNames("container severity", className)} data-test={dataTest}>
            <div className="icon">{customIcon ? customIcon : <alertSettings.alertIcon fontSize="large" />}</div>
            <div className="message">{message}</div>
            <div className="action_wrapper">
                {onClose && (
                    <div className="action">
                        <IconButton onClick={handleCloseNotification} style={{ padding: 3, width: 30, height: 30 }}>
                            <CloseOutlinedIcon fontSize="large" htmlColor={alertSettings.iconColor} />
                        </IconButton>
                    </div>
                )}
                {resultValue && (
                    <div className="result_wrapper">
                        <span className="result_message">{resultMessage}</span>
                        <span className="result_value">{resultValue}</span>
                    </div>
                )}
            </div>
        </div>
    );
};
